/**
 * defa.views.RecipientGroupCreateForm
 * A Form for editing an existing RecipientGroup.
 * @version 1.0.0
 */

import Form                     from '../../poppet/form';
import template                 from '../notifications/template-recipient-update-form.jst.html';
import Recipient                from './model-recipient';
import Recipients               from './collection-recipients';
import RecipienEmailCreateForm  from './view-recipient-email-create-form.js';
import RecipientPhoneCreateForm from './view-recipient-phone-create-form.js';
import RecipientCollectionView  from './view-recipient-collection-view.js';


const selectors = {
	inputName     : '[data-input=recipient-name]',
	addEmailButton: '[data-click=recipient-email-new]',
	addPhoneButton: '[data-click=recipient-sms-new]',
	addSection    : '[data-container=recipients-add]',
	closeButton   : '[data-button=recipients-close]',
	regionNewEmail: '[data-region=newEmail]',
	regionNewSms  : '[data-region=newSms]',
	regionEmails  : '[data-region=email-recipients]',
	regionSms     : '[data-region=sms-recipients]',
};

export const RecipientGroupUpdateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template,
	noInitValidation: true,
	ui              : {
		inputName : selectors.inputName,
		addEmail  : selectors.addEmailButton,
		addPhone  : selectors.addPhoneButton,
		addSection: selectors.addSection,
		close     : selectors.closeButton,
		newEmail  : selectors.regionNewEmail,
		newSms    : selectors.regionNewSms,
		emails    : selectors.regionEmails,
		sms       : selectors.regionSms
	},
	
	events         : {
		'click @ui.addEmail': 'onClickAddEmail',
		'click @ui.addPhone': 'onClickAddPhone'
	},
	
	regions    : {
		'email'          : '@ui.newEmail',
		'emailRecipients': '@ui.emails',
		'smsRecipients'  : '@ui.sms',
		'sms'            : '@ui.newSms',
	},
	
	templateContext: function () {
		return {
			name: this.model.get( 'name' )
		};
	},
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when a user clicks the add button for email recipients.
	 * @void
	 */
	onClickAddEmail: function ( event ) {
		event.preventDefault();
		this.hideEmailRecipients();
		let self                     = this;
		let emailRecipient           = new Recipient( { notificationGroup: this.model.get( 'id' ), unit: this.model.get( 'unit' ), type: 'EMAIL' } );
		this.listenToOnce(emailRecipient, 'sync', function (){
			self.updateEmailList();
		});
		let createEmailRecipientForm = new RecipienEmailCreateForm( { model: emailRecipient } );
		this.showChildView( 'email', createEmailRecipientForm );
	},
	
	/**
	 * Triggers when a user clicks the add button for sms recipients.
	 * @void
	 */
	onClickAddPhone: function () {
		this.hideSmsRecipients();
		event.preventDefault();
		let smsRecipient         = new Recipient( { notificationGroup: this.model.get( 'id' ), unit: this.model.get( 'unit' ), type: 'SMS' } );
		this.listenToOnce(smsRecipient, 'sync', function (){
			this.updateSmsList();
		});
		let createSmsRecipientForm = new RecipientPhoneCreateForm( { model: smsRecipient } );
		this.showChildView( 'sms', createSmsRecipientForm );
	},
	
	/**
	 * Triggers when a user clicks cancel when editing or creating a recipient.
	 * @void
	 */
	onChildviewCancel: function ( childView ) {
		this.showList( childView );
		childView.destroy();
		if(childView.model.isNew()){
			childView.model.destroy();
		}
	},
	
	/**
	 * Triggers when a user clicks save when editing or creating a recipient (if the save to backend is a success).
	 * @void
	 */
	onChildviewClose: function ( childView ) {
		this.showList( childView );
		childView.destroy();
	},
	
	/**
	 * Triggers when a user clicks a recipient.
	 * Hides the list of recipients and displays a form to edit the recipient.
	 * @void
	 */
	onChildviewOpen: function ( child ) {
		let recipient = child.model;
		if ( recipient.get( 'type' ) === 'EMAIL' ) {
			this.hideEmailRecipients();
			let createEmailRecipientForm = new RecipienEmailCreateForm( { model: recipient } );
			this.showChildView( 'email', createEmailRecipientForm );
		} else {
			this.hideSmsRecipients();
			let createSmsRecipientForm = new RecipientPhoneCreateForm( { model: recipient } );
			this.showChildView( 'sms', createSmsRecipientForm );
		}
	},
	
	/**
	 * Triggers when view renders.
	 * @void
	 */
	onRender: function () {
		this.updateEmailList();
		this.updateSmsList();
	},
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Updates/creates and displays the lists of email recipients.
	 * This is done when the view is first rendered and when a new recipient is created.
	 * @void
	 */
	updateEmailList:function(){
		let emailRecipients = this.model.getEmailRecipients();
		if ( emailRecipients.length > 0 ) {
			let recipientCollectionView = new RecipientCollectionView( {
				collection: new Recipients( emailRecipients )
			} );
			this.showChildView( 'emailRecipients', recipientCollectionView );
		} else {
			console.log( 'The Group has no email recipients' );
		}
	},
	
	
	/**
	 * Updates/creates and displays the lists of sms recipients.
	 * This is done when the view is first rendered and when a new recipient is created.
	 * @void
	 */
	updateSmsList:function(){
		let smsRecipients   = this.model.getSmsRecipients();
		if ( smsRecipients.length > 0 ) {
			let recipientCollectionView = new RecipientCollectionView( {
				collection: new Recipients( smsRecipients )
			} );
			this.showChildView( 'smsRecipients', recipientCollectionView );
		} else {
			console.log( 'The Group has no sms recipients' );
		}
	},

	
	/**
	 * Hides the list of email recipients
	 * @void
	 */
	hideEmailRecipients: function () {
		this.getRegion( 'emailRecipients' ).$el.hide();
	},
	
	/**
	 * Hides the list of sms recipients
	 * @void
	 */
	hideSmsRecipients: function () {
		this.getRegion( 'smsRecipients' ).$el.hide();
	},
	
	/**
	 * Shows the list of email recipients
	 * @void
	 */
	showEmailRecipients: function () {
		this.getRegion( 'emailRecipients' ).$el.show();
	},
	
	/**
	 * Shows the list of sms recipients
	 * @void
	 */
	showSmsRecipients: function () {
		this.getRegion( 'smsRecipients' ).$el.show();
	},
	
	showList: function ( childview ) {
		let recipient = childview.model;
		if ( recipient.get( 'type' ) === 'EMAIL' ) {
			this.showEmailRecipients();
		} else if ( recipient.get( 'type' ) === 'SMS' ) {
			this.showSmsRecipients();
		}
	},
	
	/**
	 * Validates the name input field
	 * Triggers when the user inputs something into the name input field.
	 * @void
	 */
	validate: function () {
		let currentName = this.ui.inputName.val().trim();
		return currentName !== '' && currentName !== this.model.get( 'name' );
	},
	
	/**
	 * Triggers when a user clicks the save button.
	 * Submits the form.
	 * @void
	 */
	submit() {
		let form = {
			name: this.ui.inputName.val().trim()
		};
		this.model.set( form );
		this.saveModel();
	},
	
	/**
	 * Saves the RecipientGroup model to backend.
	 * @void
	 */
	saveModel: function () {
		let self = this;
		self.busy();
		try {
			self.model.save( {}, {
				success: function ( response ) {
					console.log( 'RecipientGroup Model was saved!' );
					self.idle();
				},
				error  : function ( response ) {
					alert( 'ERROR! failed syncing' );
					console.error( 'RecipientGroup Model failed at saving...' );
					self.idle();
				}
			} );
		} catch ( err ) {
			alert( 'Failed syncing' );
			console.error( err );
			setTimeout( () => this.idle(), 1000 );
			self.model.revertModel();
		}
	},
} );
export default RecipientGroupUpdateForm;